/** Paydirt marketplace product types — labels and description guidance for sellers. */

export type ProductKind =
  | 'paydirt'
  | 'precious_metal'
  | 'precious_gemstone'
  | 'gemstone'
  | 'mineral'
  | 'tools'
  | 'machinery';

export const PRODUCT_KIND_OPTIONS: Array<{
  value: ProductKind;
  label: string;
  descriptionHint: string;
}> = [
  {
    value: 'paydirt',
    label: 'Paydirt bag',
    descriptionHint:
      'Describe bag weight, advertised metals or gems, source or wash style, and what buyers should expect. Community haul stats appear on this listing.',
  },
  {
    value: 'precious_metal',
    label: 'Precious metal',
    descriptionHint:
      'Describe metal type (gold, silver, etc.), form (nugget, flake, concentrate), weight or size, and purity or assay notes if known.',
  },
  {
    value: 'precious_gemstone',
    label: 'Precious gemstone',
    descriptionHint:
      'Describe stone type (diamond, ruby, sapphire, emerald, etc.), carat weight or size range, cut or rough form, and origin if known.',
  },
  {
    value: 'gemstone',
    label: 'Semi-precious gemstone',
    descriptionHint:
      'Describe stone type (amethyst, quartz, citrine, etc.), rough weight in grams, and origin if known.',
  },
  {
    value: 'mineral',
    label: 'Mineral / specimen',
    descriptionHint:
      'Describe mineral name, specimen size, weight, and notable features (crystals, matrix, locality).',
  },
  {
    value: 'tools',
    label: 'Tools',
    descriptionHint:
      'Describe tool type (pan, sluice, pick, classifier, etc.), size, material, condition, and what it is suited for.',
  },
  {
    value: 'machinery',
    label: 'Machinery',
    descriptionHint:
      'Describe equipment type (dredge, trommel, wash plant, etc.), capacity, power source, condition, and included accessories.',
  },
];

export function isValidProductKind(kind?: string): kind is ProductKind {
  return !!kind && PRODUCT_KIND_OPTIONS.some((o) => o.value === kind);
}

export function productKindLabel(kind?: string): string {
  const match = PRODUCT_KIND_OPTIONS.find((o) => o.value === kind);
  return match?.label ?? 'Product';
}

export function productKindDescriptionHint(kind?: ProductKind): string {
  const match = PRODUCT_KIND_OPTIONS.find((o) => o.value === kind);
  return match?.descriptionHint ?? 'Select a product type to see description guidance.';
}

export function productKindRequiresDescription(kind?: ProductKind): boolean {
  return isValidProductKind(kind);
}

export function isPaydirtProductKind(kind?: ProductKind): boolean {
  return kind === 'paydirt';
}
